import { ProductsApiAdapter } from '@modules/Products/CreateEdit/adapters/ProductsApiAdapter';
import { HttpClient } from '@shared/adapters/HttpClient';

interface ProductDimensions {
  id: number;
  name: string;
  description?: string | null;
  code: string;
  cost?: number | null;
  category_id?: number | null;
  image_hash?: string | null;
  is_box?: boolean | null;
  batch_active?: boolean | null;
  batch_type?: string | null;
  length?: string | null;
  heigth?: string | null;
  width?: string | null;
  weight?: string | null;
  weight_unit?: string | null;
  dimension_unit?: string | null;
}

interface Category {
  id: string;
  name: string;
  batch_required: boolean;
}

interface UpdateDimensionsPayload {
  length: string;
  heigth: string;
  width: string;
  weight: string;
  is_box: boolean;
  category_id: string;
  weight_unit?: string;
  dimension_unit?: string;
}

const normalizeProductResponse = (raw: Record<string, unknown>): ProductDimensions => {
  const data =
    raw?.data && typeof raw.data === 'object' && !Array.isArray(raw.data) ? (raw.data as Record<string, unknown>) : raw;

  const categoryRaw = data.category_id ?? (data.category as { id?: number | string } | undefined)?.id;

  return {
    id: Number(data.id),
    name: String(data.name ?? ''),
    description: (data.description as string | null | undefined) ?? null,
    code: String(data.code ?? ''),
    cost: data.cost != null ? Number(data.cost) : null,
    category_id: categoryRaw != null && categoryRaw !== '' ? Number(categoryRaw) : null,
    image_hash: (data.image_hash as string | null | undefined) ?? null,
    is_box: Boolean(data.is_box),
    batch_active: Boolean(data.batch_active),
    batch_type: (data.batch_type as string | null | undefined) ?? null,
    length: data.length != null ? String(data.length) : null,
    heigth: data.heigth != null ? String(data.heigth) : data.height != null ? String(data.height) : null,
    width: data.width != null ? String(data.width) : null,
    weight: data.weight != null ? String(data.weight) : null,
    weight_unit: (data.weight_unit as string | null | undefined) ?? null,
    dimension_unit: (data.dimension_unit as string | null | undefined) ?? null,
  };
};

export class ProductDimensionsApiAdapter {
  private productsAdapter: ProductsApiAdapter;

  constructor(private httpClient: HttpClient) {
    this.productsAdapter = new ProductsApiAdapter(httpClient);
  }

  async getById(productId: number): Promise<ProductDimensions> {
    const raw = await this.productsAdapter.getById(productId);
    return normalizeProductResponse(raw as Record<string, unknown>);
  }

  async getCategories(): Promise<Category[]> {
    const raw = await this.httpClient.get<any>('/categories');
    const list = Array.isArray(raw) ? raw : (raw.data ?? []);
    return list.map((cat: { id: number | string; name: string; batch_required?: boolean }) => ({
      id: String(cat.id),
      name: cat.name,
      batch_required: Boolean(cat.batch_required),
    }));
  }

  async updateDimensions(
    productId: number,
    current: ProductDimensions,
    payload: UpdateDimensionsPayload,
  ): Promise<void> {
    const parsedCost = current.cost != null ? Number(current.cost) : 0;
    const cost = Number.isFinite(parsedCost) ? parsedCost : 0;
    const batchActive = Boolean(current.batch_active);

    // Mismo endpoint y método que /products/edit: POST multipart a /products/{id}
    await this.productsAdapter.updateProduct(
      productId,
      {
        name: current.name,
        description: current.description || '',
        code: current.code,
        cost,
        categoryId: payload.category_id,
        length: payload.length,
        heigth: payload.heigth,
        width: payload.width,
        weight: payload.weight,
        weight_unit: payload.weight_unit,
        dimension_unit: payload.dimension_unit,
        batch_active: batchActive,
        batch_type: batchActive ? current.batch_type || '' : '',
      },
      current,
    );
  }
}
